<template>
  <div class="modal-dialog modal-dialog-centered modal-md" role="document">
    <div class="modal-content">
      <div class="modal-header">
        <p class="modal-title">
          <i class="fas fa-file-alt mr-2 text-secondary"></i>
          <span class="font-weight-bold text-secondary">Generating invoice</span>
        </p>
        <button
          type="button"
          class="close"
          data-dismiss="modal"
          aria-label="Close"
          :disabled="step !== 'confirm'"
        >
          <span aria-hidden="true">&times;</span>
        </button>
      </div>
      <div class="modal-body">
        <div v-if="step === 'confirm'">
          <div class="font-weight-bold confirm-alert text-center">
            You are generating
            <span v-text="countContracts"></span> new
            <span v-text="invoiceText"></span>
          </div>
          <br />
          <div
            class="text-center text-secondary confirm-info"
          >The report will be unavailable after generating the invoices</div>
        </div>
        <div v-else class="text-center">
          <loading></loading>
          <!--
            Animated with transform: scaleX() rather than width.
            `width` changes trigger layout + paint on every frame; `transform`
            is compositor-only, so rapid socket updates don't reflow the page.
          -->
          <div v-if="!preparing" class="progress mt-3">
            <div
              class="progress-bar"
              role="progressbar"
              :style="`transform: scaleX(${progressBarMax ? Math.min(progressBarValue / progressBarMax, 1) : 0})`"
            ></div>
          </div>
          <div class="text-center text-secondary mt-3 font-weight-bold">
            <template v-if="preparing">Preparing billing data…</template>
            <template v-else>{{ progressBarValue }} / {{ progressBarMax }}</template>
          </div>
          <div
            class="font-weight-bold text-center text-secondary mt-4 confirm-info"
          >Please, wait until invoices are generated.</div>
          <div
            class="text-center text-secondary confirm-sub-info"
          >Don't close or refresh this window.</div>
        </div>
      </div>
      <div class="modal-footer pr-3">
        <div class="text-right">
          <button
            type="button"
            class="btn btn-sm btn-outline-danger mr-1"
            data-dismiss="modal"
            :disabled="step !== 'confirm'"
          >Cancel</button>
          <button
            data-cy="invoice-bulk-confirm-submit-btn"
            type="button"
            class="btn btn-sm btn-primary submit-btn"
            :disabled="step !== 'confirm'"
            v-on:click="submit"
          >Generate invoices</button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.confirm-alert {
  font-size: 1.2em;
}
.confirm-info {
  font-size: 1.1em;
}
.confirm-sub-info {
  font-size: 0.7em;
}
.progress-bar {
  width: 100%;
  transform-origin: left center;
  transition: transform 0.2s ease-out;
  will-change: transform;
}
</style>

<script>
import { loading } from "../../../../../component";
import { socket, SOCKET_EVENTS } from "../../../../../service";

export default {
  get components() {
    return { loading };
  },
  props: {
    data: {
      type: Object,
      required: true,
      validator: (o) =>
        typeof o.contracts == `object` &&
        typeof o.methods == `object` &&
        typeof o.methods.submit == `function` &&
        typeof o.methods.success == `function`,
    },
  },
  data() {
    return {
      countContracts: 0,
      progressBarValue: 0,
      progressBarMax: 100,
      // true while the backend is still pre-loading; no counts to show yet
      preparing: false,
      invoiceText: "invoice.",
      step: "confirm",
    };
  },
  created() {
    this.init();
  },
  destroyed() {
    // Remove global listeners so a closed modal doesn't keep reacting to later runs
    socket.off(SOCKET_EVENTS.BULK_START, this.onBulkStart);
    socket.off(SOCKET_EVENTS.INVOICE_PROCESSED, this.onInvoiceProcessed);
  },
  methods: {
    init() {
      this.countContracts = this.data.contracts.length;
      this.progressBarValue = 0;
      this.progressBarMax = this.countContracts;
      this.preparing = false;

      socket.on(SOCKET_EVENTS.BULK_START, this.onBulkStart);
      socket.on(SOCKET_EVENTS.INVOICE_PROCESSED, this.onInvoiceProcessed);

      if (this.countContracts > 1) this.invoiceText = "invoices.";
    },
    onBulkStart(msg) {
      const { total } = JSON.parse(msg);
      this.progressBarMax = total;
      // Preload in progress - show a sentence instead of a counter, since we
      // don't have any real progress numbers to report yet.
      this.preparing = true;
    },
    onInvoiceProcessed(msg) {
      const { i, error, message, contract, stopped, processedCount, totalCount } = JSON.parse(msg)

      // First real result - real progress is available from here on
      this.preparing = false;

      if (error) {
        if (stopped) {
          // Bulk processing stopped - show detailed error with progress info
          const errorMsg = `Bulk processing stopped at contract ${contract}: ${message}\nProcessed: ${processedCount}/${totalCount} invoices\nYou can continue from where it stopped.`;
          alert(errorMsg);
          this.data.methods.success(); // Close modal to let user see where to continue
        } else {
          // Individual invoice error (shouldn't happen with new logic, but kept for safety)
          throw new Error(`${contract} ${message}`)
        }
      } else {
        this.progressBarValue = i;

        if (this.progressBarValue == this.progressBarMax) {
          this.data.methods.success();
        }
      }
    },
    async submit() {
      this.step = "generating invoices";
      this.preparing = true;
      await this.data.methods.submit();
    },
  },
};
</script>
